import type { Router } from 'vue-router'

/** Keep a local destination through sign-in and enterprise selection, without an open redirect. */
const KEY = 'os:portal-destination'
const MAX_AGE = 30 * 60 * 1000

function localPath(value: unknown): value is string {
    if (
        typeof value !== 'string' ||
        value.length > 2048 ||
        !value.startsWith('/') ||
        value.startsWith('//') ||
        /[\\\p{Cc}\s]/u.test(value)
    )
        return false
    const path = value.split(/[?#]/)[0]
    return path !== '/' && path !== '/enterprises' && !path?.startsWith('/v1/')
}

export function rememberPortalDestination(path: string): void {
    if (!localPath(path)) return
    try {
        sessionStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }))
    } catch {
        // Sign-in still works when browser storage is unavailable.
    }
}

/** Drop any kept destination: the person left without choosing, or chose to land nowhere in particular. */
export function forgetPortalDestination(): void {
    try {
        sessionStorage.removeItem(KEY)
    } catch {
        // Nothing was kept where storage is unavailable.
    }
}

/**
 * The page a switch opens in the next enterprise: the screen `fullPath` is on, without the ids, query and hash that
 * name one enterprise's rows. `/tasks/abc` is Tasks and `/members/abc/mail/def` is Members, while `/members/add` stays
 * itself. The map, or a path no screen serves, is `/`.
 */
export function pageAcrossEnterprises(router: Pick<Router, 'resolve'>, fullPath: string): string {
    const segments = (fullPath.split(/[?#]/)[0] ?? '').split('/').filter(Boolean)
    for (; segments.length > 0; segments.pop()) {
        const path = `/${segments.join('/')}`
        const resolved = router.resolve(path)
        if (resolved.matched.length > 0 && Object.keys(resolved.params).length === 0) return path
    }
    return '/'
}

/**
 * Switching enterprise from `fullPath`: the next enterprise opens on the same screen. Switching from the map keeps
 * nothing, so an older destination never takes its place.
 */
export function rememberSwitchDestination(router: Pick<Router, 'resolve'>, fullPath: string): void {
    forgetPortalDestination()
    rememberPortalDestination(pageAcrossEnterprises(router, fullPath))
}

export function takePortalDestination(): string | null {
    try {
        const raw = sessionStorage.getItem(KEY)
        sessionStorage.removeItem(KEY)
        if (!raw) return null
        const saved = JSON.parse(raw) as { path?: unknown; at?: unknown }
        if (typeof saved.at !== 'number' || Date.now() - saved.at < 0 || Date.now() - saved.at > MAX_AGE) return null
        return localPath(saved.path) ? saved.path : null
    } catch {
        return null
    }
}
